/**
 * What the policy page says about a re-assessment of this document: whether
 * one is running, which change it is reading, and — when the last one did not
 * finish — that the findings still describe the earlier text.
 *
 * Text versions are the captured snapshots the diff compares, not the uploaded
 * files the Versions tab lists, so the label says "text version" and never the
 * bare "v3" the file history uses.
 *
 * @module components/regulation/policy/PolicyReassessment.logic
 */
import { findingProvenanceUncertain } from "@repo/shared/findingStaleness";

import type { PolicyFinding } from "../../../state/governance";
import type { InspectionReassessment, InspectionRun } from "../../../state/inspectionSchemas";
import { findingCanBeRechecked } from "../finding/findingView";

/** A failed run is worth saying for a day; after that the finding warnings speak for it. */
const FAILURE_SHOWN_FOR_MS = 24 * 60 * 60 * 1000;

export type PolicyReassessmentState =
  | { readonly kind: "queued" | "running"; readonly run: InspectionRun; readonly change: string }
  | { readonly kind: "failed"; readonly run: InspectionRun; readonly change: string };

/** "text version 3 → 4", "text version 1", or — for a run that predates the scope — "the latest change". */
export function reassessmentChangeLabel(scope: InspectionReassessment | null | undefined): string {
  if (!scope || scope.toVersion === null) return "the latest change";
  return scope.fromVersion === null
    ? `text version ${scope.toVersion}`
    : `text version ${scope.fromVersion} → ${scope.toVersion}`;
}

/**
 * The newest re-assessment of this document decides what is shown. An older
 * failure is not news once a later run has started or finished, and a finished
 * run needs no banner: its findings have already refreshed in place.
 */
export function policyReassessmentState(
  runs: ReadonlyArray<InspectionRun>,
  policyId: string,
  now: number,
): PolicyReassessmentState | null {
  const latest = runs
    .filter((run) => run.reassessment?.policyId === policyId)
    .reduce<InspectionRun | null>(
      (newest, run) =>
        newest === null || Date.parse(run.startedAt) > Date.parse(newest.startedAt) ? run : newest,
      null,
    );
  if (latest === null) return null;
  const change = reassessmentChangeLabel(latest.reassessment);
  if (latest.status === "queued" || latest.status === "running")
    return { kind: latest.status, run: latest, change };
  if (
    (latest.status === "failed" || latest.status === "interrupted") &&
    now < failureShownUntil(latest)
  )
    return { kind: "failed", run: latest, change };
  return null;
}

const failureShownUntil = (run: InspectionRun): number =>
  Date.parse(run.completedAt ?? run.startedAt) + FAILURE_SHOWN_FOR_MS;

/**
 * When the shown state lapses on its own, with nothing pushed: a failure
 * banner's day running out. Null for a running state, which only an event
 * changes. The page schedules a re-render for this moment, because an idle
 * list has no timer and nothing else would take the banner down.
 */
export function policyReassessmentExpiresAt(state: PolicyReassessmentState | null): number | null {
  return state?.kind === "failed" ? failureShownUntil(state.run) : null;
}

/**
 * Whether this finding is one the running re-assessment will re-read: open,
 * the Inspector's to revise, and not yet checked against the current text.
 * A revision stamps the finding, so it drops out of this set as the run
 * reaches it — which is what lets the list show progress, one finding at a time.
 */
export const findingAwaitsReassessment = (
  finding: Pick<
    PolicyFinding,
    | "source"
    | "status"
    | "assessedRevision"
    | "policyRevision"
    | "assessedVersion"
    | "currentVersion"
  >,
): boolean => findingCanBeRechecked(finding) && findingProvenanceUncertain(finding);
